import React from 'react';
import classNames from 'classnames';
import { TextButton } from '@spothero/ui-backlog';
import SpotUtils from 'utils/spot';
import Image from 'common/image';
import withConfig from '@/config/withConfig';
import StarRatingWithCategory from 'common/star-rating-with-category';
import WalkingDistanceDisplay from 'common/spot-details/walking-distance';
import { Box, Flex, Heading, Text } from '@spothero/ui';
import DynamicVisualFlag from '../dynamic-visual-flag/DynamicVisualFlag';
import { OfficialParking } from '../official-parking/OfficialParking';
import { VISUAL_FLAG_TYPES } from 'utils/visual-flags';
import ExperimentUtils, { IMMEDIATE_AB_EXPERIMENTS } from 'utils/experiment';
import { isMoreThan24Hours } from 'utils/time';
const FacilitySummary = ({ isAdmin, showAdminLink, spot, priceLabel = 'subtotal', showFullTitle = false, showPrice = true, showImages = true, showFlair = true, appleMapsPartner, CTAs, isShowSpotsLeftVisible, showCompactFlags = false, imageWidth = 192, imageHeight = 192, staticBaseUrl, isPowerBooking, }) => {
    const { spotId, title, distance, rating: { average: averageRating, count: numberOfReviews }, selectedRate, availability, visualFlags = [], images, hasMultipleRates, } = spot;
    const { ends, starts } = selectedRate || {};
    const { available, unavailableReasons } = availability;
    const classes = classNames('FacilitySummary');
    const titleClasses = classNames('FacilitySummary-title');
    const isSearchDurationMoreThan24Hours = isMoreThan24Hours({
        starts,
        ends,
    });
    const multidayFlag = ExperimentUtils.getVariation(IMMEDIATE_AB_EXPERIMENTS.MULTIDAY_FLAG) ===
        'show_flag';
    const imgProps = {
        width: imageWidth,
        height: imageHeight,
        alt: `Photo of ${title}`,
        lazyLoad: true,
        ...(images?.[0]
            ? { cloudinaryTemplate: images[0].urlTemplate }
            : { src: `${staticBaseUrl}img/missing-image.png` }),
    };
    const renderPrice = () => {
        let displayPriceFormatted = 'N/A';
        if (selectedRate) {
            const { currencyCode, value } = isPowerBooking
                ? selectedRate.subTotal || {}
                : selectedRate.advertisedPrice || {};
            displayPriceFormatted = SpotUtils.formatRatePrice({
                price: value,
                currencyType: currencyCode,
                postFix: hasMultipleRates ? '+' : '',
            });
        }
        return (<div className="price" dangerouslySetInnerHTML={{ __html: displayPriceFormatted }}/>);
    };
    return (<Flex flexFlow="column" height="100%" data-testid={`FacilitySummary-${encodeURIComponent(title)}`}>
            {showFlair &&
            visualFlags
                .filter(el => el.type === VISUAL_FLAG_TYPES.DYNAMIC)
                .map((flag, i) => (<DynamicVisualFlag key={`dynamic:${spotId}:${i}`} title={flag.title} isCompact={showCompactFlags} {...flag}/>))}
            <Flex justifyContent="space-between" position="relative" padding="16px" height="100%" className={classes} data-testid="FacilitySummary-Images">
                {showImages && (<Box height={{ base: '80px', tablet: '96px' }} width={{ base: '80px', tablet: '96px' }} marginRight="16px" cursor="pointer" flexShrink="0" sx={{
                img: {
                    borderRadius: '8px',
                },
            }} data-testid="images-wrapper">
                        <Image {...imgProps}/>
                    </Box>)}
                <Flex flexGrow="1" minWidth="0" minHeight={isShowSpotsLeftVisible && { base: 32, tablet: 28 }} flexFlow="column">
                    <Flex>
                        <Box flexGrow="1" minWidth="0" 
    // Charcoal color from .detail not found in design system so rolled with closest
    color="gray.900">
                            {showFlair &&
            visualFlags
                .filter(el => el.type ===
                VISUAL_FLAG_TYPES.OFFICIAL_PARKING ||
                (el.type ===
                    VISUAL_FLAG_TYPES.MULTI_DAY &&
                    isSearchDurationMoreThan24Hours &&
                    multidayFlag))
                .map((flag, i) => (<OfficialParking key={`flag:${spotId}:${i}`} {...flag}/>))}
                            <Heading as="h2" marginBottom="0">
                                <Text as="span" className={titleClasses} fontWeight="semibold" minHeight="auto" lineHeight="1.2" display="block" overflow={showFullTitle ? 'visible' : 'hidden'} textOverflow={showFullTitle ? 'initial' : 'ellipsis'} whiteSpace={showFullTitle ? 'normal' : ''} padding={showPrice ? '0' : ''} data-testid="FacilitySummary-title">
                                    {title}
                                </Text>
                            </Heading>
                            {isAdmin && showAdminLink && (<Box fontSize="sm" display={{
                base: 'none',
                tablet: 'inline-block',
            }} marginLeft="8px">
                                    <TextButton href={`/admin/spots/${spotId}`}>
                                        Facility Admin
                                    </TextButton>
                                </Box>)}
                            <Box paddingBottom={1}>
                                <StarRatingWithCategory numberOfReviews={numberOfReviews} starRating={averageRating}/>
                            </Box>
                            {distance && !appleMapsPartner && (<WalkingDistanceDisplay linearMeters={distance.linearMeters} walkingMeters={distance.walkingMeters} walkingDuration={distance.durationSeconds}/>)}
                            {isAdmin && !available && (<Text as="span" fontSize="sm" display="block">
                                    <sup>*</sup> {unavailableReasons.join('; ')}
                                </Text>)}
                        </Box>
                        {showPrice && available && (<Box className="FacilitySummary-price-container" data-testid="FacilitySummary-Price">
                                {/* TODO - MR - Wanted to migrate this to Price component but this functionality is different.
            May be worth migrating over to that component going forward. */}
                                {renderPrice()}
                                <Text as="span">{priceLabel}</Text>
                            </Box>)}
                    </Flex>
                    <Box flexGrow="1" minHeight={2}/>
                    <Box className="controls" data-testid="FacilitySummary-CTA">
                        {CTAs}
                    </Box>
                </Flex>
            </Flex>
        </Flex>);
};
export default withConfig(['staticBaseUrl'])(FacilitySummary);
